/**
 * Metric — a headline figure, typeset the way the product typesets figures.
 *
 * Three rules, all visible on any product screen:
 *   - the number is large and bright, the unit small and muted, and the two
 *     sit on a shared baseline — "5 210 500 ₽" is one number and one label,
 *     not a single string;
 *   - a supporting line underneath carries the comparison ("из 10 000 000");
 *   - digits are tabular, so a column of these doesn't shiver.
 *
 * @format
 */

import {type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text, type TypographyRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {TABULAR} from '../helpers';
import type {PaletteTone} from '../content';

export type MetricSize = 'xl' | 'l' | 'm';

/**
 * `xl` выходит за типографическую шкалу: самый крупный роль-стиль —
 * `heading.xl` (32px), а есть места, где число само по себе является
 * содержанием блока и должно читаться через всю страницу. Там кегль
 * задаётся явно поверх роли.
 */
const XL_FONT_SIZE = '56px';
const XL_LINE_HEIGHT = '60px';

/**
 * The product sets figures at 32/36 for a hero number and 24/28 inside a
 * card. Note `m` maps to `heading.l`, not `heading.m` (18px) — that is a
 * label size and reads as body copy rather than as a headline figure.
 */
const VALUE_ROLE: Record<MetricSize, TypographyRole> = {
  xl: 'heading.xl',
  l: 'heading.xl',
  m: 'heading.l',
};

export interface MetricProps {
  /**
   * Подпись НАД числом. Короткая, с большой буквы: «План», «Было». Стоит
   * сверху и мелко, потому что читателю нужна цифра — подпись лишь говорит,
   * что это за цифра.
   */
  label?: ReactNode;
  value: ReactNode;
  /** Unit or currency, set small and muted beside the figure. */
  unit?: ReactNode;
  /** Comparison line under the figure. */
  caption?: ReactNode;
  /** Tone of the figure. Omit for neutral. */
  tone?: PaletteTone;
  /** `xl` = 56px, `l` = 32px, `m` = 24px. Default `l`. */
  size?: MetricSize;
  /**
   * Метрика — самостоятельный блок, а не показатель в ряду себе подобных.
   *
   * По умолчанию подпись и единица приглушены: в отчёте метрика стоит
   * внутри карточки, у которой есть свой заголовок, и второй яркий текст
   * над числом спорил бы с ним. Там же, где метрика и ЕСТЬ содержание
   * блока — витрина хранилища, где кроме двух чисел ничего нет, — подпись
   * работает заголовком: её кегль поднимается на ступень, а приглушение
   * снимается со всего блока. Тусклая подпись над таким числом читается
   * как сноска к нему.
   */
  prominent?: boolean;
}

const Value = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone?: PaletteTone; $size: MetricSize}>(({theme, $tone, $size}) => ({
  ...text(VALUE_ROLE[$size]),
  ...TABULAR,
  color: $tone ? theme.palette[$tone].main : surfaceVar('onSurface'),
  ...($size === 'xl' ? {fontSize: XL_FONT_SIZE, lineHeight: XL_LINE_HEIGHT} : null),
}));

const Unit = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$prominent: boolean}>(({$prominent}) => ({
  ...text($prominent ? 'body.l' : 'body.m'),
  ...TABULAR,
  color: $prominent ? surfaceVar('onSurface') : surfaceVar('onSurfaceMuted'),
}));

const Caption = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$prominent: boolean}>(({$prominent}) => ({
  ...text($prominent ? 'body.l' : 'body.m'),
  ...TABULAR,
  color: $prominent ? surfaceVar('onSurface') : surfaceVar('onSurfaceLight'),
}));

const Label = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$prominent: boolean}>(({$prominent}) => ({
  ...text($prominent ? 'heading.m' : 'body.m'),
  color: $prominent ? surfaceVar('onSurface') : surfaceVar('onSurfaceMuted'),
}));

/**
 * Метрика занимает ровно свою ширину, а не всю строку.
 *
 * `Stack` по контракту растянут на 100 % контейнера — это правильно для
 * вертикального блока, но метрика чаще стоит в ряду с соседями. С полной
 * шириной каждая из них забирала строку целиком, и ряд «бюджет ÷ цена =
 * план» разъезжался по вертикали, сколько ни правь `wrap` на самом ряду.
 */
const Root = styled(Stack)({
  width: 'auto',
  flex: '0 0 auto',
});

export function Metric({
  label,
  value,
  unit,
  caption,
  tone,
  size = 'l',
  prominent = false,
}: MetricProps) {
  return (
    // Подпись-заголовок отходит от числа на ступень больше: она перестаёт
    // быть его хвостом и становится названием блока.
    <Root gap={prominent ? 'xs' : 'tight'}>
      {label != null && <Label $prominent={prominent}>{label}</Label>}
      <Row gap='s' alignY='baseline' wrap='nowrap'>
        <Value $tone={tone} $size={size}>
          {value}
        </Value>
        {unit != null && <Unit $prominent={prominent}>{unit}</Unit>}
      </Row>
      {caption != null && <Caption $prominent={prominent}>{caption}</Caption>}
    </Root>
  );
}
